// AdminPanel/orgSearch — input rules shared by the Org ID and Email finders.
//
// Validation here is about giving the operator a useful message, NOT about
// safety. The query reaches Postgres as a BOUND PARAMETER
// (ListOrganizationsV3 uses $1..$6 via pgx/sqlc), so a value containing
// quotes or SQL keywords is matched as text and cannot alter the statement —
// verified against the live API: `' OR 1=1 --` returns zero rows and the
// tenants table is untouched. Rejecting those characters would only stop
// someone searching for an org whose name legitimately contains an apostrophe.

// Zitadel org ids are numeric snowflakes (e.g. 378884552388509999), and V3
// matches them with `t.id = :search` — EXACT. A partial id silently returns
// nothing, so tell the operator rather than letting them wonder.
const ORG_ID_RE = /^[0-9]{6,25}$/;

// Deliberately loose: the field is a SEARCH box that also does partial
// matching, so "@combirds.com" or "dharmadev+" are valid queries. Only the
// obviously-wrong shapes are rejected.
const EMAIL_FRAGMENT_RE = /^[A-Za-z0-9._%+@'-]{2,254}$/;

// Whitespace is the common paste artefact — a trailing space off a spreadsheet
// cell, or a newline off a terminal. Trim it silently; an id or address never
// legitimately carries one, and failing the search over an invisible character
// is a bad experience.
export function normalizeQuery(raw) {
  return (raw || '').trim();
}

export function validateOrgId(raw) {
  const q = normalizeQuery(raw);
  if (!q) return {ok: false, error: 'Enter an org id to search.'};
  if (/\s/.test(q)) return {ok: false, error: 'An org id contains no spaces — check the value pasted.'};
  if (!ORG_ID_RE.test(q)) {
    return {ok: false, error: 'Org ids are numeric (e.g. 378884552388509999). Paste the complete id — a partial id will not match.'};
  }
  return {ok: true, value: q};
}

export function validateEmail(raw) {
  const q = normalizeQuery(raw);
  if (!q) return {ok: false, error: 'Enter an email address to search.'};
  if (/\s/.test(q)) return {ok: false, error: 'An email address contains no spaces — check the value pasted.'};
  if (q.length < 2) return {ok: false, error: 'Enter at least two characters.'};
  if (!EMAIL_FRAGMENT_RE.test(q)) {
    return {ok: false, error: 'That does not look like an email address or a fragment of one.'};
  }
  return {ok: true, value: q};
}

// Headers (sender ids) are 3-11 chars of letters/digits, upper-cased on both
// entry paths (migrate-sync and the onboarding form). Matching is a PARTIAL
// ILIKE, so a fragment like "EDU" legitimately returns every tenant using a
// header that contains it — that is the point of the tab, since an operator
// usually has a header off an SMS and wants to know who sends under it.
//
// One header can belong to MANY tenants: EDUMRC is the shared default across
// hundreds. Callers must expect a list, not a single row.
const HEADER_FRAGMENT_RE = /^[A-Za-z0-9]{2,16}$/;

export function validateHeader(raw) {
  const q = normalizeQuery(raw);
  if (!q) return {ok: false, error: 'Enter a header (sender id) to search.'};
  if (/\s/.test(q)) {
    return {ok: false, error: 'A header contains no spaces — check the value pasted.'};
  }
  if (!HEADER_FRAGMENT_RE.test(q)) {
    return {ok: false, error: 'Headers are letters and digits only (e.g. EDUMRC). Punctuation will not match.'};
  }
  // Stored upper-case; searching lower would still match via ILIKE, but
  // normalising keeps what the operator sees consistent with the result.
  return {ok: true, value: q.toUpperCase()};
}

// DLT principal entity ids are 19-digit numeric registrations. Matched EXACTLY
// by ListOrganizationsV3 (`t.config->>'dlt_principal_entity_id' = :search`),
// because a partial would match hundreds of tenants through the shared
// placeholder 1234567890123456789.
//
// One DLT id can legitimately cover MANY tenants — a reseller registers once
// and onboards several customers under it — so expect a list.
const DLT_ID_RE = /^[0-9]{10,25}$/;

export function validateDltId(raw) {
  const q = normalizeQuery(raw);
  if (!q) return {ok: false, error: 'Enter a DLT principal entity ID to search.'};
  if (/\s/.test(q)) return {ok: false, error: 'A DLT ID contains no spaces — check the value pasted.'};
  if (!DLT_ID_RE.test(q)) {
    return {ok: false, error: 'DLT IDs are numeric (e.g. 1701174731403059285). Paste the complete id — a partial will not match.'};
  }
  return {ok: true, value: q};
}
